import type {
  FillGradient,
  FillMode,
  ObjectGlow,
  ObjectShadow,
  SceneObject,
} from '../types';
import {
  parseFillGradient,
  parseFillMode,
  parseObjectGlow,
  parseObjectShadow,
} from './objectVisualStyle';

export type TextAlign = 'left' | 'center' | 'right' | 'justify';

export interface TextBodyData {
  content?: string;
  fontSize?: number;
  textAlign?: TextAlign;
  fill?: string;
  fillMode?: FillMode;
  fillGradient?: FillGradient;
  shadow?: ObjectShadow;
  glow?: ObjectGlow;
}

export function parseTextBody(body: string | undefined | null): TextBodyData {
  if (!body) return {};
  try {
    const parsed = JSON.parse(body) as TextBodyData;
    if (parsed && typeof parsed === 'object') {
      parsed.fillMode = parseFillMode(parsed.fillMode) ?? parsed.fillMode;
      parsed.fillGradient = parseFillGradient(parsed.fillGradient) ?? parsed.fillGradient;
      parsed.shadow = parseObjectShadow(parsed.shadow) ?? parsed.shadow;
      parsed.glow = parseObjectGlow(parsed.glow) ?? parsed.glow;
      return parsed;
    }
  } catch {
    // legacy plain-text body
  }
  return { content: body };
}

export function textBodyFromSceneObject(
  obj: Pick<
    SceneObject,
    'name' | 'fontSize' | 'textAlign' | 'fill' | 'fillMode' | 'fillGradient' | 'shadow' | 'glow'
  >
): TextBodyData {
  const data: TextBodyData = { content: obj.name };
  if (obj.fontSize != null) data.fontSize = obj.fontSize;
  if (obj.textAlign != null) data.textAlign = obj.textAlign;
  if (obj.fill != null) data.fill = obj.fill;
  if (obj.fillMode != null) data.fillMode = obj.fillMode;
  if (obj.fillGradient != null) data.fillGradient = obj.fillGradient;
  if (obj.shadow != null) data.shadow = obj.shadow;
  if (obj.glow != null) data.glow = obj.glow;
  return data;
}

export function serializeTextBody(data: TextBodyData): string {
  return JSON.stringify(data);
}

export function resolveTextFieldsFromBody(
  type: string,
  body: string | undefined,
  fallbackName?: string
): Partial<
  Pick<
    SceneObject,
    'name' | 'fontSize' | 'textAlign' | 'fill' | 'fillMode' | 'fillGradient' | 'shadow' | 'glow'
  >
> {
  if (type !== 'text') return {};
  const parsed = parseTextBody(body);
  return {
    name: parsed.content ?? fallbackName ?? 'Text',
    fontSize: parsed.fontSize,
    textAlign: parsed.textAlign,
    fill: parsed.fill,
    fillMode: parsed.fillMode,
    fillGradient: parsed.fillGradient,
    shadow: parsed.shadow,
    glow: parsed.glow,
  };
}

export function isFillNone(
  objOrFill?: string | Pick<SceneObject, 'fill' | 'fillMode'> | null
): boolean {
  if (objOrFill == null) return false;
  if (typeof objOrFill === 'string') return objOrFill === 'none';
  if (objOrFill.fillMode === 'none') return true;
  if (objOrFill.fillMode === 'solid' || objOrFill.fillMode === 'gradient') return false;
  return objOrFill.fill === 'none';
}

export function isBorderNone(
  obj: Pick<SceneObject, 'strokeStyle' | 'strokeWidth' | 'strokeSides'>
): boolean {
  if ((obj.strokeStyle ?? 'solid') === 'none' || (obj.strokeWidth ?? 2) <= 0) return true;
  const sides = obj.strokeSides;
  if (!sides) return false;
  return !sides.top && !sides.right && !sides.bottom && !sides.left;
}
